/**
 * Input Component
 * @author Muhammad Arsan
 * @description A reusable input component using Material-UI's Input component
 */

import React, { useRef, useEffect } from "react";
import { TextField, InputAdornment } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import type { InputFieldProps } from "../types/inputfield";
import { TextFieldStyles } from "../styles/TextField.styles";
import {
	formFieldLabelClassName,
	formSelectPlaceholderClassName,
	formSelectValueClassName,
	mergeSelectSx,
} from "@config/theme/formControls";
import { FormFieldHelperText, FormFieldLabel } from "@components/common/FormField";
import { SelectFieldWrapper } from "../../Select/shared/SelectFieldWrapper";

const InputField: React.FC<InputFieldProps> = ({
	id,
	label,
	type = "text",
	placeholder,
	value,
	onChange,
	disabled = false,
	error = false,
	helperText = "",
	startIcon,
	endIcon,
	multiline = false,
	rows,
	className,
	labelClassName,
	onClick,
	isClickable,
	"aria-label": ariaLabel,
	"aria-describedby": ariaDescribedby,
	required,
	size = "small",
	...rest
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		InputProps: customInputProps,
		select: isSelect,
		hideSelectChevron = false,
		selectControlHeight,
		sx: sxProp,
		SelectProps: selectPropsOverride,
		...restProps
	} = rest;

	useEffect(() => {
		if (type === "date" && inputRef.current) {
			const input = inputRef.current;
			if (value && value.toString().trim() !== "") {
				input.style.color = "#000";
				input.style.webkitTextFillColor = "#000";
			} else {
				input.style.color = "#6B7280";
				input.style.webkitTextFillColor = "#6B7280";
			}
		}
	}, [type, value]);

	const startAdornment = startIcon ? (
		<InputAdornment
			position="start"
			sx={{
				marginRight: -1,
				"& .MuiInputAdornment-positionStart": {
					marginRight: 0,
				},
			}}>
			{startIcon}
		</InputAdornment>
	) : undefined;

	const selectRenderValue = (selected: unknown): React.ReactNode => {
		if (!selected || selected === "") {
			return <span className={formSelectPlaceholderClassName}>{placeholder}</span>;
		}

		const selectedOption = React.Children.toArray(restProps.children).find(
			child =>
				React.isValidElement(child) &&
				(child.props as Record<string, unknown>)?.value?.toString() === selected?.toString(),
		) as React.ReactElement | undefined;

		const children = (selectedOption?.props as Record<string, unknown>)?.children;
		if (children && typeof children === "string") {
			return <span className={formSelectValueClassName}>{children}</span>;
		}
		return <span className={formSelectValueClassName}>{String(selected)}</span>;
	};

	return (
		<div className="min-w-0 max-w-full">
			{label && (
				<FormFieldLabel htmlFor={id} required={required} className={labelClassName ?? formFieldLabelClassName}>
					{label}
				</FormFieldLabel>
			)}
			{isSelect ? (
				<>
					<SelectFieldWrapper
						controlHeight={selectControlHeight}
						className={hideSelectChevron ? "[&>button:last-of-type]:hidden" : undefined}>
						<TextField
							id={id}
							className={`${className ?? ""} ${isClickable ? "cursor-pointer" : ""}`}
							fullWidth
							variant="outlined"
							type={type}
							placeholder={placeholder}
							value={value ?? ""}
							onChange={onChange}
							onClick={onClick}
							disabled={disabled}
							error={error}
							helperText={undefined}
							rows={rows}
							multiline={multiline}
							aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
							aria-describedby={ariaDescribedby}
							aria-invalid={error ? "true" : "false"}
							sx={mergeSelectSx(error, sxProp as SxProps<Theme> | undefined)}
							SelectProps={{
								displayEmpty: true,
								renderValue: selectRenderValue,
								...selectPropsOverride,
							}}
							InputProps={{
								startAdornment,
								endAdornment: endIcon ? <InputAdornment position="end">{endIcon}</InputAdornment> : undefined,
								...(customInputProps || {}),
							}}
							inputRef={inputRef}
							select
							{...restProps}
						/>
					</SelectFieldWrapper>
					<FormFieldHelperText error={error}>{helperText}</FormFieldHelperText>
				</>
			) : (
				<TextField
					id={id}
					className={`${className ?? ""} ${isClickable ? "cursor-pointer" : ""}`}
					fullWidth
					variant="outlined"
					type={type}
					placeholder={placeholder}
					value={value ?? ""}
					onChange={onChange}
					onClick={onClick}
					disabled={disabled}
					error={error}
					helperText={helperText}
					rows={rows}
					multiline={multiline}
					aria-label={ariaLabel || (typeof label === "string" ? label : undefined)}
					aria-describedby={ariaDescribedby}
					aria-invalid={error ? "true" : "false"}
					sx={TextFieldStyles(isClickable, size, error, multiline)}
					InputProps={{
						startAdornment,
						endAdornment: endIcon ? <InputAdornment position="end">{endIcon}</InputAdornment> : undefined,
						...(customInputProps || {}),
					}}
					inputRef={inputRef}
					{...restProps}
				/>
			)}
		</div>
	);
};

export default InputField;
